import 'reflect-metadata';
import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import { RequestFailure } from '@Repository/RequestAction';
import {
  ArmyListRepository,
  RequestArmyListSuccess,
} from '@Repository/builder';
import {
  ArmyRepository,
  RequestArmyListVersioningSuccess,
  RequestArmyVersioningsSuccess,
  RequestVersionVersioningsSuccess,
  VersionRepository,
} from '@Repository/ninthAge';
import { Nullable, VersionVersioning } from '@Models';

export const CACHE_VERSION = 'v8';
export const CACHE_NAME = '9thbuilder-cache-' + CACHE_VERSION;

@injectable()
export class CacheManager {
  @inject(TYPES.Repository.VersionRepository)
  private _versionRepository!: VersionRepository;
  @inject(TYPES.Repository.ArmyRepository)
  private _armyRepository!: ArmyRepository;
  @inject(TYPES.Repository.ArmyListRepository)
  private _armyListRepository!: ArmyListRepository;

  public async syncCache({
    locale,
  }: {
    locale: Nullable<string>;
  }): Promise<void> {
    console.log('Start sync cache');
    await this.reloadArmies({ locale: locale });
    //await this.reloadArmyLists({locale: locale});
  }

  public async loadLastVersion({
    locale,
  }: {
    locale: Nullable<string>;
  }): Promise<VersionVersioning[]> {
    const response = await this._versionRepository.getVersioning({
      locale: locale,
    });
    if (response instanceof RequestFailure) {
      console.log('Impossible to load Version versioning.');
      return [];
    }

    const success = response as RequestVersionVersioningsSuccess;
    return success.payload.reduce((a, b) => {
      if (null == a.find((item) => item.game === b.game)) {
        a.push(b);
        return a;
      }

      return a.map((item) => {
        if (item.game === b.game && item.id < b.id) {
          return b;
        }
        return item;
      });
    }, new Array<VersionVersioning>());
  }

  public async reloadArmies({
    locale,
  }: {
    locale: Nullable<string>;
  }): Promise<void> {
    const response = await this._armyRepository.getVersioning({
      locale: locale,
    });
    if (response instanceof RequestFailure) {
      console.log('Impossible to load Army versioning.');
      return;
    }

    const success = response as RequestArmyVersioningsSuccess;

    const lastVersions = await this.loadLastVersion({ locale: locale });

    for (const item of success.payload) {
      console.log(`Verify sync Army: ${item.id}`);
      const updatedAt = await this._armyRepository.getUpdatedAt({
        dataId: item.id,
        locale: locale,
      });

      if (!lastVersions.some((v) => v.id === item.versionId)) {
        if (null == updatedAt) continue;
      }

      console.log(`Verify sync Army:${updatedAt} >= ${item.updatedAt}`);
      if (updatedAt && updatedAt >= item.updatedAt) continue;

      console.log(`-> Start sync Army: ${item.id}`);
      await this._armyRepository.getArmy({ armyId: item.id, locale: locale });
    }
  }

  public async reloadArmyLists({
    locale,
  }: {
    locale: Nullable<string>;
  }): Promise<void> {
    const response = await this._armyListRepository.getVersioning({
      locale: locale,
    });
    if (response instanceof RequestFailure) {
      console.log('Impossible to load ArmyList versioning.');
      return;
    }

    const success = response as RequestArmyListVersioningSuccess;

    for (const item of success.payload) {
      console.log(`Verify sync ArmyList: ${item.id}`);
      const updatedAt = await this._armyListRepository.getUpdatedAt({
        dataId: item.id,
        locale: locale,
      });
      if (updatedAt != null && updatedAt >= item.updatedAt) continue;

      console.log(`-> Start sync ArmyList: ${item.id}`);
      const response = await this._armyListRepository.getArmyList({
        id: item.id,
        locale: locale,
      });
      if (response instanceof RequestFailure) {
        console.log(`-> Impossible to sync ArmyList: ${item.id}`);
        continue;
      }

      const armyList = (response as RequestArmyListSuccess).payload;
      console.log(`Verify exist Army: ${armyList.armyId}`);
      const hasArmy = await this._armyRepository.hasCachedEntity({
        dataId: armyList.armyId,
        locale: locale,
      });
      if (!hasArmy) {
        console.log(`-> Start sync Army: ${armyList.armyId}`);
        await this._armyRepository.getArmy({
          armyId: armyList.armyId,
          locale: locale,
        });
      }
    }
  }
}
